import { useEffect, useState } from "react";
import { captureToofiAnalytics } from "@/lib/toofi-analytics";
import {
  deleteToofiArtifact,
  ensureToofiArtifactMigration,
  getToofiArtifact,
  listToofiArtifacts,
  saveToofiArtifact,
  setToofiArtifactPinned,
  type SaveToofiArtifactInput,
} from "@/lib/toofi-artifact-store";
import { TOOFI_ARTIFACT_STORAGE_EVENT, type ToofiArtifactRecord, type ToofiArtifactRecordIndexItem } from "@/lib/toofi-artifact-types";

export function useToofiArtifacts() {
  const [artifacts, setArtifacts] = useState<ToofiArtifactRecordIndexItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      setIsLoading(true);
      await ensureToofiArtifactMigration();
      const next = await listToofiArtifacts();
      if (!cancelled) {
        setArtifacts(next);
        setIsLoading(false);
      }
    };

    void refresh();

    const onRefresh = () => {
      void refresh();
    };

    window.addEventListener(TOOFI_ARTIFACT_STORAGE_EVENT, onRefresh);
    window.addEventListener("storage", onRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener(TOOFI_ARTIFACT_STORAGE_EVENT, onRefresh);
      window.removeEventListener("storage", onRefresh);
    };
  }, []);

  return {
    artifacts,
    isLoading,
    saveArtifact: async (input: SaveToofiArtifactInput) => {
      const saved = await saveToofiArtifact(input);
      captureToofiAnalytics("toofi_artifact_saved", {
        artifact_id: saved.id,
        artifact_kind: saved.kind,
        artifact_scope: saved.scope,
        surface_origin: saved.surfaceOrigin,
      });
      return saved;
    },
    openArtifact: async (artifactId: string) => {
      const artifact = await getToofiArtifact(artifactId);
      if (artifact) {
        captureToofiAnalytics("toofi_artifact_opened", {
          artifact_id: artifact.id,
          artifact_kind: artifact.kind,
          artifact_scope: artifact.scope,
          surface_origin: artifact.surfaceOrigin,
        });
      }
      return artifact;
    },
    deleteArtifact: async (artifactId: string) => {
      await deleteToofiArtifact(artifactId);
    },
    setArtifactPinned: async (artifactId: string, pinned: boolean) => {
      await setToofiArtifactPinned(artifactId, pinned);
    },
  };
}
